import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { PositionListItem, PositionOption, PositionRequest } from './positionTypes';

/** The catalog's list query adds the salary-type and usage facets to the shared contract. */
interface PositionQuery extends ListQuery {
  /** Comma-separated: `general`, `health`, `none`. */
  type?: string;
  /** Comma-separated: `used`, `unused`. */
  usage?: string;
}

function toQueryString(query: PositionQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.type !== undefined) params.set('type', query.type);
  if (query.usage !== undefined) params.set('usage', query.usage);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const positionsApi = {
  /** Soft-deletes every title in the catalog at once; resolves with how many were deleted. */
  clear: () => api<number>('/api/positions/clear', { method: 'POST' }),

  list: (query: PositionQuery, signal?: AbortSignal) =>
    api<PagedResult<PositionListItem>>(`/api/positions${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<PositionListItem>(`/api/positions/${id}`, { signal }),

  /**
   * Active titles for the post form's picker. Unpaged on purpose — a picker that stopped at a
   * page boundary would drop choices without saying so.
   */
  options: (signal?: AbortSignal) => api<PositionOption[]>('/api/positions/options', { signal }),

  create: (request: PositionRequest) =>
    api<PositionListItem>('/api/positions', { method: 'POST', body: request }),

  update: (id: string, request: PositionRequest) =>
    api<PositionListItem>(`/api/positions/${id}`, { method: 'PUT', body: request }),

  /** Soft delete. Posts drawn from the title keep the snapshot they were created with. */
  deactivate: (id: string) => api<void>(`/api/positions/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/positions/${id}/restore`, { method: 'POST' }),
};
